import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { selectWysiwigEditorEnabled } from 'core/userContext/selectors';
import { OnGetContentRef } from 'modules/dragAndDropEditor/types';
import {
  EmailContentDefinition,
  HtmlEditor,
  InAppContentDefinition,
  MessageMedium,
} from 'modules/graphql/types';
import { useLocalesQueryContext } from 'modules/campaigns/lib/contexts/LocalesQueryContext';

import {
  EditorView,
  InitTemplateTrigger,
  MediumDataKey,
  TemplateAndContentFormData,
  TemplateAndContentFormDataLocale,
  UpdateMediumFieldsType,
} from '../common/types';
import {
  getContentEditor,
  getCorrectlyCasedLocale,
  getInitTemplateTrigger,
  getTemplateHasContent,
} from '../common/utils';
import { EditableFieldsContext, EditableFieldsContextState } from './EditableFieldsContext';
import { useQueryParamContext } from './QueryParamContext';
import { useTemplateQueryContext } from './TemplateQueryContext';

export const EditableFieldsContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const {
    formState: { defaultValues: defaultTemplate },
    reset: resetLiveTemplate,
    setValue,
    watch,
  } = useFormContext();
  const editTemplate = watch();

  const primitiveEditTemplate = JSON.stringify(editTemplate || {});
  const primitiveDefaultTemplate = JSON.stringify(defaultTemplate || {});
  const { locale: preParsedLocale } = useQueryParamContext();
  const { isLoading, medium, previousDataLocale, previousDataTemplateId, template } =
    useTemplateQueryContext();
  const { allProjectLocales: projectLocales, templateDefaultLocale } = useLocalesQueryContext();

  const locale = getCorrectlyCasedLocale(preParsedLocale, projectLocales);
  const defaultLocaleTag = templateDefaultLocale?.tag || '';
  const isChangingLocales =
    locale === defaultLocaleTag || locale === ''
      ? !['', defaultLocaleTag].includes(previousDataLocale || '')
      : locale !== previousDataLocale;

  // stores a trigger for Drag and Drop (BEE) Save method (generates html), accepts a callback as a param
  const getDragAndDropContentRef = useRef<OnGetContentRef>();
  // flag to skip unsaved changes check on navigation (within react)
  const skipUnsavedChangesCheck = useRef<boolean>(false);

  // has the content loaded at least once
  const isInitialContentLoaded = useRef<boolean>(false);
  // is the settings form in edit mode
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  // for Email, the current content editor view
  const [contentEditorView, setContentEditorView] = useState<EditorView>(EditorView.HTML);
  // template content suggested by Next Best Action
  const [suggestedContent, setSuggestedContent] = useState<UpdateMediumFieldsType | undefined>();

  // for Email and InApp, the html editor
  const wysiwygEditorEnabled =
    useSelector(selectWysiwigEditorEnabled) || medium === MessageMedium.InApp;
  const htmlMediumData = (editTemplate?.mediumData || {}) as
    | EmailContentDefinition
    | InAppContentDefinition;
  const contentEditor = getContentEditor(
    medium,
    htmlMediumData.htmlEditor,
    !!htmlMediumData.beeBuilderMetadata,
    htmlMediumData.html,
    wysiwygEditorEnabled,
  );

  const [dragAndDropInitTs, setDragAndDropInitTs] = useState<number>(0);
  const [dragAndDropInitValue, setDragAndDropInitValue] = useState<string>(
    htmlMediumData.beeBuilderMetadata || '{}',
  );

  const isTemplateDirty = useMemo(() => {
    const templateDirty = primitiveEditTemplate !== primitiveDefaultTemplate;
    return templateDirty;
  }, [primitiveEditTemplate, primitiveDefaultTemplate]);

  const hasContent = useMemo(() => {
    const { mediumData } = JSON.parse(primitiveEditTemplate);
    return getTemplateHasContent(medium, mediumData);
  }, [medium, primitiveEditTemplate]);

  const handleSetDragAndDropInitTs = (initTs = Date.now()) => {
    setDragAndDropInitTs(initTs);
  };

  const handleSetDragAndDropInitValue = (initValue?: string) => {
    const htmlMedium = (editTemplate?.mediumData || {}) as
      | EmailContentDefinition
      | InAppContentDefinition;
    setDragAndDropInitValue(initValue || htmlMedium.beeBuilderMetadata || '{}');
  };

  const setCurrentMediumFields = (updatedFields: UpdateMediumFieldsType) => {
    Object.entries(updatedFields).forEach(([name, value]) => {
      setValue(`mediumData.${name}`, value);
    });
  };

  const setCurrentMediumField = (fieldName: MediumDataKey, value: any) =>
    setCurrentMediumFields({ [fieldName]: value });

  const setCurrentTemplateField = (fieldName: keyof TemplateAndContentFormData, value: any) => {
    setValue(fieldName, value);
  };

  /* Update the live template whenever the template changes (id will change in the case of cloning,
   * some or all of the template will change in the case of refetch (after a successful save, etc))
   */
  useEffect(() => {
    if (!isLoading) {
      const currentLocaleContentObj = JSON.parse(template);

      const isFirstLoad = !isInitialContentLoaded.current;
      isInitialContentLoaded.current = true;
      // note: in some circumstances, previousDataTemplateId will already be populated by the time
      // isLoading is false for the first time, so isFirstLoad was added as a secondary check
      const isTemplateFirstLoad =
        currentLocaleContentObj.templateId &&
        (currentLocaleContentObj.templateId !== previousDataTemplateId || isFirstLoad);
      if (currentLocaleContentObj.templateId) {
        // will only be called if the template exists, so medium will be valid
        const templateTrigger = getInitTemplateTrigger(isTemplateFirstLoad, isChangingLocales);
        const { mediumData = {} } = currentLocaleContentObj;
        const htmlMedium = mediumData as EmailContentDefinition | InAppContentDefinition;
        if (
          (htmlMedium.htmlEditor === HtmlEditor.Bee || !!htmlMedium.beeBuilderMetadata) &&
          [InitTemplateTrigger.LocaleUpdate, InitTemplateTrigger.TemplateFirstLoad].includes(
            templateTrigger,
          )
        ) {
          setDragAndDropInitValue(htmlMedium.beeBuilderMetadata || '{}');
        }
      }
    }
  }, [isChangingLocales, isLoading, medium, previousDataTemplateId, template]);

  const context: EditableFieldsContextState = {
    contentEditor,
    contentEditorView,
    getDragAndDropContentRef,
    dragAndDropInitTs,
    dragAndDropInitValue,
    hasContent,
    initialTemplate: defaultTemplate as TemplateAndContentFormDataLocale,
    isDirty: isTemplateDirty,
    isEditMode,
    liveTemplate: editTemplate as TemplateAndContentFormDataLocale,
    resetLiveTemplate,
    selectedMessageMedium: medium,
    setContentEditorView,
    setCurrentMediumField,
    setCurrentMediumFields,
    setCurrentTemplateField,
    setDragAndDropInitTs: handleSetDragAndDropInitTs,
    setDragAndDropInitValue: handleSetDragAndDropInitValue,
    setIsEditMode,
    setSuggestedContent,
    skipUnsavedChangesCheck,
    suggestedContent,
  };

  return (
    <EditableFieldsContext.Provider value={context}>{children}</EditableFieldsContext.Provider>
  );
};
